import type { CommissionRule, LaneCard } from 'app/pages/CommissionManager/types';
import type { AccommodationCondition } from 'app/pages/CommissionManager/types';
import { CommissionProductSelectMode } from './types';

export type ApiCommissionRulePayload = {
  id: number;
  name: string;
  active: boolean;
  include: ApiFieldItem[];
  exclude: ApiFieldItem[];
  feeIds: number[];
};

export type ApiFieldItem = Record<string, unknown>;

const fmt = (prefix: string, id: number) => `${prefix}-${id}`;

function commissionKind(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function userCards(cards: LaneCard[]): LaneCard[] {
  return cards.filter((c) => !c.isMirror);
}

function laneCardToFieldItem(card: LaneCard): ApiFieldItem | null {
  const cond = card.condition;
  const nullWeeks = { minWeeks: null, maxWeeks: null };

  switch (card.productType) {
    case 'Course': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'course', mode: CommissionProductSelectMode.Any, courseIds: [], ...nullWeeks };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'course',
          mode: CommissionProductSelectMode.Select,
          courseIds: cond.selectedIds.map((id) => fmt('Course', id)),
          ...nullWeeks,
        };
      }
      return null;
    }
    case 'Transfer': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'transfer', mode: CommissionProductSelectMode.Any, transferIds: [] };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'transfer',
          mode: CommissionProductSelectMode.Select,
          transferIds: cond.selectedIds.map((id) => fmt('Transfer', id)),
        };
      }
      return null;
    }
    case 'Accommodation': {
      const c = cond as AccommodationCondition | undefined;
      if (!c || c.mode === 'ANY') {
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Any,
          kind: null,
          accommodationIds: [],
          ...nullWeeks,
        };
      }
      if (c.mode === 'CHOOSE_TYPE') {
        const kind = commissionKind(c.typeId);
        if (kind == null) return null;
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Type,
          kind,
          accommodationIds: [],
          ...nullWeeks,
        };
      }
      if (c.mode === 'SELECT' && c.selectedIds?.length) {
        return {
          field: 'accommodation',
          mode: CommissionProductSelectMode.Select,
          kind: null,
          accommodationIds: c.selectedIds.map((id) => fmt('Accommodation', id)),
          ...nullWeeks,
        };
      }
      return null;
    }
    case 'AddOn': {
      const c = cond as AccommodationCondition | undefined;
      if (!c || c.mode === 'ANY') {
        return { field: 'addOn', mode: CommissionProductSelectMode.Any, kind: null, addOnIds: [] };
      }
      if (c.mode === 'CHOOSE_TYPE') {
        const kind = commissionKind(c.typeId);
        if (kind == null) return null;
        return { field: 'addOn', mode: CommissionProductSelectMode.Type, kind, addOnIds: [] };
      }
      if (c.mode === 'SELECT' && c.selectedIds?.length) {
        return {
          field: 'addOn',
          mode: CommissionProductSelectMode.Select,
          kind: null,
          addOnIds: c.selectedIds.map((id) => fmt('AddOn', id)),
        };
      }
      return null;
    }
    case 'Fees': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'fee', mode: CommissionProductSelectMode.Any, feeIds: [] };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'fee',
          mode: CommissionProductSelectMode.Select,
          feeIds: cond.selectedIds.map((id) => fmt('Fee', id)),
        };
      }
      return null;
    }
    case 'Package': {
      if (!cond || cond.mode === 'ANY') {
        return { field: 'package', mode: CommissionProductSelectMode.Any, packageIds: [] };
      }
      if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds.length > 0) {
        return {
          field: 'package',
          mode: CommissionProductSelectMode.Select,
          packageIds: cond.selectedIds.map((id) => fmt('Package', id)),
        };
      }
      return null;
    }
    default:
      return null;
  }
}

function ruleNumericId(rule: CommissionRule): number {
  if (rule.id.startsWith('rule-')) return 0;
  const n = Number(rule.id);
  return Number.isFinite(n) ? n : 0;
}

const nullWeeks = { minWeeks: null, maxWeeks: null };

function includeAnyStubForField(field: string): ApiFieldItem | null {
  switch (field) {
    case 'course':
      return { field: 'course', mode: CommissionProductSelectMode.Any, courseIds: [], ...nullWeeks };
    case 'transfer':
      return { field: 'transfer', mode: CommissionProductSelectMode.Any, transferIds: [] };
    case 'accommodation':
      return {
        field: 'accommodation',
        mode: CommissionProductSelectMode.Any,
        kind: null,
        accommodationIds: [],
        ...nullWeeks,
      };
    case 'addOn':
      return { field: 'addOn', mode: CommissionProductSelectMode.Any, kind: null, addOnIds: [] };
    case 'fee':
      return { field: 'fee', mode: CommissionProductSelectMode.Any, feeIds: [] };
    case 'package':
      return { field: 'package', mode: CommissionProductSelectMode.Any, packageIds: [] };
    default:
      return null;
  }
}

function ensureIncludeForEveryExcludeField(include: ApiFieldItem[], exclude: ApiFieldItem[]): ApiFieldItem[] {
  const seen = new Set(include.map((i) => String(i.field)));
  const stubs: ApiFieldItem[] = [];
  for (const ex of exclude) {
    const f = String(ex.field);
    if (seen.has(f)) continue;
    const stub = includeAnyStubForField(f);
    if (!stub) continue;
    seen.add(f);
    stubs.push(stub);
  }
  return [...include, ...stubs];
}

export function commissionRuleToApiPayload(rule: CommissionRule): ApiCommissionRulePayload {
  let include = userCards(rule.include)
    .map(laneCardToFieldItem)
    .filter((x): x is ApiFieldItem => x != null);
  const exclude = userCards(rule.exclude)
    .map(laneCardToFieldItem)
    .filter((x): x is ApiFieldItem => x != null);

  include = ensureIncludeForEveryExcludeField(include, exclude);

  const feeIds = rule.feesIncludeAny ? [] : rule.fees.map((f) => f.id);

  return {
    id: ruleNumericId(rule),
    name: rule.name || '',
    active: rule.active !== false,
    include,
    exclude,
    feeIds,
  };
}

export type CommissionGroupOfferRequest = { rules: ApiCommissionRulePayload[] };

export function commissionRulesToOfferRequest(rules: CommissionRule[]): CommissionGroupOfferRequest {
  return { rules: rules.map(commissionRuleToApiPayload) };
}